import React, { useCallback } from 'react';
import T from 'i18n';
import moment from 'moment';
import { addAlpha } from 'utils/color';
import { SELECTION_COLOR } from 'features/viewing/AcusoftViewer/AcusoftViewerControl/constants';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { selectQuery } from 'common/selectors';

type AnnotationItemProps = {
  highlight: any;
  selectedHighlight: any;
  scrollToHighlightHandler: (highlight: any) => void;
  sortMode: any;
};

export default ({
  highlight,
  selectedHighlight,
  scrollToHighlightHandler,
  sortMode,
}: AnnotationItemProps) => {
  const queryParam = useSelector(selectQuery);
  const history = useHistory();
  const location = useLocation();
  const isFocusView =
    queryParam?.focus === 'true' ||
    queryParam?.focus === true ||
    queryParam?.focus === '1' ||
    Boolean(queryParam?.focused);

  const handleFocusViewLinkClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      const href = highlight?.annotation?.href;
      if (!isFocusView || !href) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (event.button !== 0) return;
      const targetUrl = (() => {
        try {
          return new URL(href, window.location.origin);
        } catch {
          return null;
        }
      })();
      if (!targetUrl) return;
      const match = targetUrl.pathname.match(/\/case\/[^/]+\/files\/([^/?#]+)/);
      if (!match) return;

      event.preventDefault();
      event.stopPropagation();

      const targetFileId = match[1];
      if (!targetFileId) return;

      const searchParams = new URLSearchParams(location.search || '');
      searchParams.set('focused', targetFileId);
      searchParams.delete('focus');
      const carryParams = ['goToPage', 'tab', 'startPage', 'pageCount', 'globalPagePrefix'];
      carryParams.forEach((param) => {
        const value = targetUrl.searchParams.get(param);
        if (value) searchParams.set(param, value);
        else searchParams.delete(param);
      });
      const nextSearch = searchParams.toString();
      history.push({
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
        hash: location.hash || '',
        state: location.state,
      });
    },
    [highlight?.annotation?.href, history, isFocusView, location],
  );

  return (
    <li
      key={highlight.annotation.uid}
      className="sidebar-view__highlight"
      style={{ ...(sortMode && { cursor: 'move' }) }}
      onClick={() => {
        if (!sortMode) scrollToHighlightHandler(highlight);
      }}
      {...(selectedHighlight &&
        selectedHighlight.annotation.uid === highlight.annotation.uid && {
          style: { backgroundColor: addAlpha(SELECTION_COLOR, 70) },
        })}
    >
      <div>
        {highlight.annotation.data.comment && highlight.annotation.type !== 'TextSignature' && (
          <div>
            <strong>{highlight.annotation.data.comment || ''}</strong>
          </div>
        )}
        {highlight.annotation.data.linkId && highlight.annotation.type !== 'TextAnnotation' && (
          <div>
            <a
              onClick={(e) => {
                handleFocusViewLinkClick(e);
                e.stopPropagation();
              }}
              href={highlight.annotation.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {highlight.annotation.data.linkId}
            </a>
          </div>
        )}
        {highlight.annotation.type === 'TextAnnotation' && highlight.annotation.text && (
          <div>{highlight.annotation.text}</div>
        )}
        {highlight.annotation.selectedText ? (
          <blockquote style={{ marginTop: '0.5rem' }}>
            {`${highlight.annotation.selectedText.slice(0, 90).trim()}…`}
          </blockquote>
        ) : null}
        {highlight.annotation.data.image ? (
          <div className="highlight__image" style={{ marginTop: '0.5rem' }}>
            <img src={highlight.annotation.data.image} alt={'Screenshot'} />
          </div>
        ) : null}
        {(highlight.annotation.type === 'TextSignature' ||
          highlight.annotation.type === 'TextAnnotation') && (
          <span>
            <span>
              <strong>
                {highlight.annotation.type === 'TextSignature'
                  ? T.translate('common.signedBy')
                  : T.translate('common.referenceBy')}
                :{' '}
              </strong>
              {highlight.createdBy.name}
              <span style={{ float: 'right' }}>
                <strong>{T.translate('case.team')}: </strong>
                {highlight.createdBy.teamName}
              </span>
            </span>
            <br />
            <strong>{T.translate('common.when')}: </strong>
            {moment(highlight.createdBy.date).format('DD/MM/YYYY HH:mm')}
          </span>
        )}
      </div>
      <div className="highlight__location">Page {highlight.annotation.pageNumber}</div>
    </li>
  );
};
